import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppButton, AppModal, Callout, Checkbox, FormField, HelpText, IconButton, Input,
  NativeSelect, Textarea,
} from "../ui";

/**
 * QuizCategoryFormModal — create or edit one quiz category (or subcategory).
 *
 * The one category form: the Categories page, the Question Bank page and the
 * add-question page all open this, so a category made from any of them is the
 * same row with the same fields.
 *
 * `category` is the row being edited, or null to create. A folder from the
 * question bank's library tree carries only its name and icon, so when the row
 * has no `tags` the full category is read before the form is filled — saving a
 * half-loaded row would blank its description and tags.
 *
 * A failed save stays in the dialog with the server's reason ("A category named
 * … already exists"), rather than closing it and losing what was typed.
 */

// The submit button sits in the modal's footer, outside the form element, so it
// needs the form's id to submit it.
const FORM_ID = "quiz-category-form";

const FIELD_ROW = "tw-grid tw-grid-cols-[100px_1fr] tw-gap-3 max-[480px]:tw-grid-cols-1";

const emptyForm = {
  name: "", description: "", icon: "", tags: [], parentCategory: null,
  // Marketplace (library/SA mode only)
  published: false, pricingModel: "free", pricingPeriod: "", pricingAmount: 0,
};

const formFrom = (cat) => {
  const mp = cat.marketplace || {};
  const pr = mp.pricing || cat.pricing || {};
  return {
    name: cat.name || "",
    description: cat.description || "",
    icon: cat.icon || "",
    tags: cat.tags || [],
    parentCategory: cat.parentCategory || null,
    published: !!(mp.published ?? cat.published),
    pricingModel: pr.model || "free",
    pricingPeriod: pr.period || "",
    pricingAmount: pr.amount || 0,
  };
};

/**
 * A list of tags plus the field that adds one.
 *
 * The whole box focuses as a unit — `focus-within` puts the ring on the wrapper,
 * so the inner input carries none of its own, which is why it is not an `Input`.
 * Clicking anywhere in the box reaches the input because it is the flex item
 * that grows.
 */
function TagInput({ tags, value, onValueChange, onKeyDown, onBlur, onRemove }) {
  return (
    <div className="tw-flex tw-min-h-[2.4rem] tw-flex-wrap tw-items-center tw-gap-1 tw-rounded-md tw-border tw-border-solid tw-border-input-line tw-bg-input tw-px-2 tw-py-1.5 tw-cursor-text focus-within:tw-border-primary/40 focus-within:tw-shadow-focus">
      {tags.map((tag) => (
        <span
          key={tag}
          className="tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-primary/10 tw-py-0.5 tw-pl-2 tw-pr-1 tw-text-[0.72rem] tw-font-medium tw-text-primary"
        >
          {tag}
          <IconButton
            icon={X}
            label={`Remove ${tag}`}
            variant="ghost"
            size="xs"
            className="tw-h-4 tw-w-4 tw-text-primary"
            onClick={() => onRemove(tag)}
          />
        </span>
      ))}
      <input
        type="text"
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        placeholder={tags.length === 0 ? "Placement, IELTS, Campus Test…" : ""}
        aria-label="Add a tag"
        className="tw-min-w-[80px] tw-flex-1 tw-border-0 tw-bg-transparent tw-p-0.5 tw-text-[0.85rem] tw-text-input-fg tw-outline-none placeholder:tw-text-content-subtle"
      />
    </div>
  );
}

export default function QuizCategoryFormModal({
  show,
  onHide,
  category = null,
  parentId = null,
  isLibrary = false,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingRow, setLoadingRow] = useState(false);
  const [error, setError] = useState("");

  const editing = !!category?._id;

  // Fill the form each time the dialog opens — never carry the last one's
  // typing into the next.
  useEffect(() => {
    if (!show) return undefined;
    setError("");
    setTagInput("");
    if (!editing) {
      setForm({ ...emptyForm, parentCategory: parentId });
      return undefined;
    }
    setForm(formFrom(category));
    if (category.tags !== undefined) return undefined;

    let cancelled = false;
    setLoadingRow(true);
    apiClient
      .get(`/quiz-categories/${category._id}`, { headers: { "X-Branch-Id": "all" } })
      .then(({ data }) => { if (!cancelled && data?.category) setForm(formFrom(data.category)); })
      .catch(() => { if (!cancelled) setError("Could not load this category's details."); })
      .finally(() => { if (!cancelled) setLoadingRow(false); });
    return () => { cancelled = true; };
    // Re-fill only when a different row is opened, not when the parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, category?._id, parentId]);

  const close = () => { if (!saving) onHide?.(); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || saving || loadingRow) return;
    setSaving(true);
    setError("");

    const payload = {
      name: form.name.trim(),
      description: form.description,
      icon: form.icon,
      tags: tagInput.trim() && !form.tags.includes(tagInput.trim())
        ? [...form.tags, tagInput.trim()]
        : form.tags,
      parentCategory: form.parentCategory,
    };
    if (isLibrary) {
      payload.marketplace = {
        published: form.published,
        pricing: {
          model: form.pricingModel,
          period: form.pricingModel === "paid" ? form.pricingPeriod : "",
          amount: form.pricingModel === "paid" ? Number(form.pricingAmount) || 0 : 0,
          currency: "INR",
        },
      };
    }

    try {
      const { data } = editing
        ? await apiClient.put(`/quiz-categories/${category._id}`, payload)
        : await apiClient.post("/quiz-categories", payload);
      setSaving(false);
      onSaved?.(data?.category || null, { created: !editing });
    } catch (err) {
      setSaving(false);
      setError(err.response?.data?.message || "Could not save the category. Please try again.");
    }
  };

  // ── Tag input helpers ───────────────────────────────────────────────
  const addTag = (tag) => {
    const trimmed = tag.trim();
    if (trimmed && !form.tags.includes(trimmed)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, trimmed] }));
    }
    setTagInput("");
  };
  const removeTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };
  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTag(tagInput);
    }
    if (e.key === "Backspace" && !tagInput && form.tags.length > 0) {
      removeTag(form.tags[form.tags.length - 1]);
    }
  };

  const isSub = !!form.parentCategory;
  const title = editing
    ? `Edit ${isSub ? "subcategory" : "category"}`
    : isSub ? "Add subcategory" : "Add category";

  return (
    <AppModal
      show={show}
      onHide={close}
      title={title}
      size="md"
      footer={
        <>
          <AppButton variant="outline" type="button" onClick={close} disabled={saving}>
            Cancel
          </AppButton>
          <AppButton
            variant="default"
            type="submit"
            form={FORM_ID}
            disabled={saving || loadingRow || !form.name.trim()}
          >
            {saving ? "Saving…" : editing ? "Save changes" : "Create"}
          </AppButton>
        </>
      }
    >
      {/* The submit button lives in the modal's footer, outside the <form>, so it
          is wired by `form=` rather than by nesting — which also keeps Enter
          submitting from any field. */}
      <form id={FORM_ID} onSubmit={handleSave} className="tw-flex tw-flex-col tw-gap-4">
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        <FormField label="Name" required>
          <Input
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Aptitude, GRE Quant, IELTS Reading"
            disabled={loadingRow}
            autoFocus
          />
        </FormField>

        <FormField label="Description">
          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            placeholder="Brief description (optional)"
            disabled={loadingRow}
          />
        </FormField>

        <div className={FIELD_ROW}>
          <FormField label="Icon" hint="Single emoji shown on the card.">
            <Input
              value={form.icon}
              onChange={(e) => setForm((p) => ({ ...p, icon: e.target.value }))}
              placeholder="📝"
              maxLength={4}
              className="tw-text-center tw-text-[1.1rem]"
              disabled={loadingRow}
            />
          </FormField>

          <FormField
            label={<>Tags <span className="tw-font-normal tw-text-muted-fg">— press Enter to add</span></>}
            className="tw-min-w-0"
          >
            <TagInput
              tags={form.tags}
              value={tagInput}
              onValueChange={setTagInput}
              onKeyDown={handleTagKeyDown}
              onBlur={() => tagInput && addTag(tagInput)}
              onRemove={removeTag}
            />
          </FormField>
        </div>

        {isLibrary && (
          <div className="tw-flex tw-flex-col tw-gap-2.5 tw-border-0 tw-border-t tw-border-solid tw-border-line tw-pt-3.5">
            <Checkbox
              label="Publish this folder to the marketplace"
              labelClassName="tw-font-semibold"
              checked={form.published}
              onChange={(e) => setForm((p) => ({ ...p, published: e.target.checked }))}
            />

            <div className={FIELD_ROW}>
              <FormField label="Pricing">
                <NativeSelect
                  value={form.pricingModel}
                  onChange={(e) => setForm((p) => ({ ...p, pricingModel: e.target.value }))}
                  options={[
                    { value: "free", label: "Free" },
                    { value: "paid", label: "Paid" },
                  ]}
                />
              </FormField>
              {form.pricingModel === "paid" && (
                <>
                  <FormField label="Billing">
                    <NativeSelect
                      value={form.pricingPeriod}
                      onChange={(e) => setForm((p) => ({ ...p, pricingPeriod: e.target.value }))}
                      options={[
                        { value: "", label: "One-time" },
                        { value: "monthly", label: "Monthly" },
                        { value: "yearly", label: "Yearly" },
                      ]}
                    />
                  </FormField>
                  <FormField label="Amount (₹)">
                    <Input
                      type="number"
                      min="0"
                      value={form.pricingAmount}
                      onChange={(e) => setForm((p) => ({ ...p, pricingAmount: e.target.value }))}
                    />
                  </FormField>
                </>
              )}
            </div>
            <HelpText>
              Folders stay hidden from institutes until published. Paid folders can be viewed
              but need a purchase (or a granted entitlement) before questions can be imported.
            </HelpText>
          </div>
        )}
      </form>
    </AppModal>
  );
}
